// 上り字幕（STT partial/final）の取得＋暫定ターンテイキング転送（設計v2 §2.2/§2.4・AC-8/16）。
// VS は STT 結果を WS では push しない（#213 時点の契約）ため、通話中のみ
// GET /v1/calls/:callId を 500ms でポーリングして transcript 差分と lastPartial を拾う。
//
// INTERIM_TURN_TAKING: VS に STT final→konecco の自動結線が無いため、検出した final を
// POST /v1/calls/:callId/text で konecco へ転送する（暫定）。ターンゲート付き:
//   turnIdle --(final)--> 送信 --> turnInFlight（以後の final はバッファ）
//   turnInFlight --(chat-update final / job-event / error(utteranceId一致) / 20s timeout)-->
//     バッファあり→連結して1通送信（inFlight 継続）／なし→turnIdle
// #212 が VS 側結線を入れたら既定 OFF→撤去（AI-WF266 へ通告済み・voice-config.ts 参照）。

import { getCallSummary, postCallText } from './voice-api';

export interface SubtitleFeedHandlers {
  onPartial: (text: string | null) => void;
  onFinal: (text: string, forwarded: 'pending' | 'held' | 'off') => void;
  onForwardResult: (utteranceId: string | null, ok: boolean, detail?: string) => void;
  /** summary が 404（セッション消滅）になった */
  onGone: () => void;
  isAutoForwardOn: () => boolean;
}

const POLL_INTERVAL_MS = 500;
const TURN_TIMEOUT_MS = 20_000;
const MIN_SEND_GAP_MS = 1_500;

export class SubtitleFeed {
  private timer: number | null = null;
  private seenFinals = 0;
  private stopped = false;

  // ターンゲート
  private inFlightUtteranceId: string | null = null;
  private buffer: string[] = [];
  private lastSentText = '';
  private lastSentAt = 0;
  private turnTimer: number | null = null;

  constructor(
    private callId: string,
    private handlers: SubtitleFeedHandlers,
  ) {}

  start(): void {
    if (this.timer != null) return;
    this.timer = window.setInterval(() => void this.pollOnce(), POLL_INTERVAL_MS);
  }

  /** 1回分のポーリング（切断 drain 時にも明示的に呼ぶ） */
  async pollOnce(): Promise<void> {
    if (this.stopped) return;
    let summary;
    try {
      summary = await getCallSummary(this.callId);
    } catch {
      return; // 一時的な失敗は次周期に任せる
    }
    if (this.stopped) return;
    if (summary == null) {
      this.handlers.onGone();
      return;
    }
    const transcript = summary.transcript ?? [];
    for (; this.seenFinals < transcript.length; this.seenFinals++) {
      const text = (transcript[this.seenFinals]?.text ?? '').trim();
      if (text === '') continue; // 無音セグメント等の空 final は捨てる（R1 MF-2）
      this.handleFinal(text);
    }
    this.handlers.onPartial(summary.lastPartial?.trim() || null);
  }

  /** 現ターンの終端（chat-update final / job-event / error / timeout）を通知される */
  notifyTurnEnd(): void {
    this.clearTurnTimer();
    this.inFlightUtteranceId = null;
    if (this.buffer.length > 0) {
      const joined = this.buffer.join(' ');
      this.buffer = [];
      void this.send(joined);
    }
  }

  /** 転送中 utteranceId（error イベントの相関用） */
  get inFlightId(): string | null {
    return this.inFlightUtteranceId;
  }

  stop(): void {
    this.stopped = true;
    if (this.timer != null) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
    this.clearTurnTimer();
  }

  private handleFinal(text: string): void {
    if (!this.handlers.isAutoForwardOn()) {
      this.handlers.onFinal(text, 'off');
      return;
    }
    // 直前送信と同一テキストの連続 final は dedupe（R1 MF-2）
    if (text === this.lastSentText && Date.now() - this.lastSentAt < MIN_SEND_GAP_MS * 4) {
      this.handlers.onFinal(text, 'off');
      return;
    }
    if (this.inFlightUtteranceId != null || Date.now() - this.lastSentAt < MIN_SEND_GAP_MS) {
      this.buffer.push(text);
      this.handlers.onFinal(text, 'held');
      return;
    }
    this.handlers.onFinal(text, 'pending');
    void this.send(text);
  }

  private async send(text: string): Promise<void> {
    this.lastSentText = text;
    this.lastSentAt = Date.now();
    this.inFlightUtteranceId = 'sending';
    this.armTurnTimer();
    try {
      const res = await postCallText(this.callId, text);
      this.inFlightUtteranceId = res.utteranceId;
      this.handlers.onForwardResult(res.utteranceId, true);
    } catch (e) {
      this.inFlightUtteranceId = null;
      this.clearTurnTimer();
      this.handlers.onForwardResult(null, false, e instanceof Error ? e.message : 'forward failed');
    }
  }

  private armTurnTimer(): void {
    this.clearTurnTimer();
    this.turnTimer = window.setTimeout(() => this.notifyTurnEnd(), TURN_TIMEOUT_MS);
  }

  private clearTurnTimer(): void {
    if (this.turnTimer != null) {
      window.clearTimeout(this.turnTimer);
      this.turnTimer = null;
    }
  }
}
